import React from "react";
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import StarIcon from "@mui/icons-material/Star";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import {
  formatName,
  get_t_str,
} from "../../../../helpers/helpers";
import {
  formatDateReadableWithDayOfWeek,
  formatDateReadableMonthAndDay,
} from "../../requests/utilities/helpers";

const MIN_PEAK_WINDOW_SURVEYS = 3;
const PEAK_WINDOW_HOURS = 2;
const PEAK_WINDOW_STEP = 0.25;
const TEAM_MEMBER_LIST_LIMIT = 5;

export default function OverviewTabDisplay(props) {
  const { surveys, trend, teamStats, timePatterns, ratingDistribution, dateRange } = props;

  // Handle no data case
  if (surveys.length === 0) {
    return (
      <div className="bg-white p-12 rounded-lg shadow border border-gray-200 text-center">
        <p className="text-gray-500 text-lg">No surveys found for the selected date range</p>
        <p className="text-gray-400 text-sm mt-2">Try selecting a longer date range or check back later</p>
      </div>
    );
  }

  // Calculate trend direction by comparing first half vs second half of selected range
  // Use YYYY-MM-DD string comparison to avoid UTC day shifts
  const formatDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const now = new Date();
  const rangeStartDate = new Date(now);
  rangeStartDate.setDate(now.getDate() - dateRange);
  const midpointDate = new Date(now);
  midpointDate.setDate(now.getDate() - Math.floor(dateRange / 2));

  const rangeStartStr = formatDateString(rangeStartDate);
  const midpointStr = formatDateString(midpointDate);

  // First half of range (older data)
  // Compare YYYY-MM-DD strings directly to avoid timezone issues
  const firstHalfSurveys = surveys.filter(s => s.date >= rangeStartStr && s.date < midpointStr);

  // Second half of range (recent data)
  const secondHalfSurveys = surveys.filter(s => s.date >= midpointStr);

  const firstHalfAvg = firstHalfSurveys.length > 0
    ? firstHalfSurveys.reduce((sum, s) => sum + s.rating, 0) / firstHalfSurveys.length
    : 0;

  const secondHalfAvg = secondHalfSurveys.length > 0
    ? secondHalfSurveys.reduce((sum, s) => sum + s.rating, 0) / secondHalfSurveys.length
    : 0;

  const trendDirection = (secondHalfAvg - firstHalfAvg).toFixed(2);
  const hasSufficientData = firstHalfSurveys.length >= 3 && secondHalfSurveys.length >= 3;

  // Peak performance time (best rated 2-hour window with 15-min precision)
  const findPeak2HourWindow = () => {
    if (surveys.length < MIN_PEAK_WINDOW_SURVEYS) {
      return { startHour: 0, avgRating: 0, count: 0 };
    }

    let bestWindow = { startHour: 0, avgRating: 0, count: 0 };

    // Check every 15-minute interval (0, 0.25, 0.5, 0.75, 1, 1.25, ...)
    for (let start = 0; start <= 24 - PEAK_WINDOW_HOURS; start += PEAK_WINDOW_STEP) {
      const endTime = start + PEAK_WINDOW_HOURS;

      // Filter surveys within this 2-hour window
      const windowSurveys = surveys.filter(s =>
        s.decimal_hour >= start && s.decimal_hour < endTime
      );

      if (windowSurveys.length < MIN_PEAK_WINDOW_SURVEYS) continue;

      // Calculate average rating for this window
      const totalRating = windowSurveys.reduce((sum, s) => sum + s.rating, 0);
      const avgRating = totalRating / windowSurveys.length;
      const roundedAvgRating = parseFloat(avgRating.toFixed(2));

      if (
        roundedAvgRating > bestWindow.avgRating ||
        (roundedAvgRating === bestWindow.avgRating && windowSurveys.length > bestWindow.count)
      ) {
        bestWindow = {
          startHour: start,
          avgRating: roundedAvgRating,
          count: windowSurveys.length
        };
      }
    }

    return bestWindow;
  };

  const peakWindow = findPeak2HourWindow();

  // Top team member
  const topPerformer = teamStats.length > 0 ? teamStats[0] : null;

  // Get top and bottom performers without showing the same person in both lists.
  const topPerformers = teamStats.slice(0, TEAM_MEMBER_LIST_LIMIT);
  const topPerformerIds = new Set(topPerformers.map(member => member.id));
  const bottomPerformers = teamStats
    .filter(member => !topPerformerIds.has(member.id))
    .slice(-TEAM_MEMBER_LIST_LIMIT);
  const getTeamMemberRank = (memberId) => teamStats.findIndex(member => member.id === memberId) + 1;

  return (
    <div className="space-y-6">
      {/* Key Insights Banner */}
      <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Key Insights</h2>
        <div className="grid grid-cols-3 gap-6">

          {/* Rating Trend */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-600">Rating Trend</p>
            <div className="flex items-center mt-1">
              {hasSufficientData ? (
                <>
                  <p className={`text-2xl font-bold ${parseFloat(trendDirection) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {parseFloat(trendDirection) >= 0 ? '+' : ''}{trendDirection}
                  </p>
                  {parseFloat(trendDirection) >= 0 ? (
                    <TrendingUpIcon className="text-green-600 ml-2" />
                  ) : (
                    <TrendingDownIcon className="text-red-600 ml-2" />
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-500">Insufficient data</p>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">Recent {Math.floor(dateRange / 2)} days vs previous {Math.floor(dateRange / 2)}</p>
          </div>

          {/* Peak Performance Time */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-600">Peak Performance Time</p>
            {timePatterns.length > 0 && peakWindow.count >= MIN_PEAK_WINDOW_SURVEYS ? (
              <>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {get_t_str(peakWindow.startHour)} - {get_t_str(peakWindow.startHour + PEAK_WINDOW_HOURS)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {peakWindow.avgRating} avg rating ({peakWindow.count} {peakWindow.count === 1 ? 'survey' : 'surveys'})
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-500 mt-1">Insufficient data</p>
                <p className="text-xs text-gray-500 mt-1">
                  Need {MIN_PEAK_WINDOW_SURVEYS} surveys in a {PEAK_WINDOW_HOURS}-hour window
                </p>
              </>
            )}
          </div>

          {/* Top Team Member */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-600">Top Team Member</p>
            {topPerformer ? (
              <>
                <p className="text-lg font-bold text-gray-900 mt-1">
                  {formatName(topPerformer.name)}
                </p>
                <div className="flex items-center mt-1">
                  <StarIcon className="text-yellow-400 text-sm" sx={{ fontSize: "16px" }} />
                  <p className="text-sm text-gray-600 ml-1">
                    {topPerformer.avgRating} avg ({topPerformer.totalSurveys} surveys)
                  </p>
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-500 mt-1">No data</p>
            )}
          </div>

        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-2 gap-6">

        {/* Rating Trend Over Time - LINE CHART */}
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Rating Trend Over Time</h3>
          {trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(date) => {
                    try {
                      return formatDateReadableMonthAndDay(date);
                    } catch (e) {
                      return date;
                    }
                  }}
                />
                <YAxis domain={[0, 5]} />
                <Tooltip
                  labelFormatter={(date) => {
                    try {
                      return formatDateReadableWithDayOfWeek(date);
                    } catch (e) {
                      return date;
                    }
                  }}
                  formatter={(value, name, props) => {
                    if (name === "Average Rating") {
                      const count = props.payload.count;
                      return [`${value} (${count} ${count === 1 ? 'survey' : 'surveys'})`, name];
                    }
                    return [value, name];
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="avgRating"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  name="Average Rating"
                  dot={{ fill: '#3b82f6' }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              No trend data available
            </div>
          )}
        </div>

        {/* Rating Distribution - PIE CHART */}
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Rating Distribution</h3>
          {ratingDistribution.some(d => d.value > 0) ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={ratingDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent, value }) =>
                    percent > 0 ? `${name}: ${(percent * 100).toFixed(0)}% (${value} ${value === 1 ? 'survey' : 'surveys'})` : ''
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {ratingDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              No rating data available
            </div>
          )}
        </div>

      </div>

      {/* Performance Tables */}
      <div className="grid grid-cols-2 gap-6">

        {/* Top Performers */}
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <span className="text-green-600 mr-2">⭐</span>
            Top Performing Team Members
          </h3>
          {topPerformers.length > 0 ? (
            <div className="space-y-3">
              {topPerformers.map((member, index) => (
                <div key={member.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center font-bold text-sm mr-3">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{formatName(member.name)}</p>
                      <p className="text-xs text-gray-600">{member.role}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center justify-end">
                      <StarIcon className="text-yellow-400 text-sm mr-1" sx={{ fontSize: "16px" }} />
                      <p className="font-bold text-gray-900">{member.avgRating}</p>
                    </div>
                    <p className="text-xs text-gray-600">{member.totalSurveys} surveys</p>
                    <p className="text-xs text-green-600">{member.positivePercentage}% positive</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No team member data available</p>
          )}
        </div>

        {/* Bottom Performers */}
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <span className="text-yellow-600 mr-2">⚠️</span>
            Team Members Needing Support
          </h3>
          {bottomPerformers.length > 0 ? (
            <div className="space-y-3">
              {bottomPerformers.map((member, index) => (
                <div key={member.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-yellow-600 text-white flex items-center justify-center font-bold text-sm mr-3">
                      {getTeamMemberRank(member.id)}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{formatName(member.name)}</p>
                      <p className="text-xs text-gray-600">{member.role}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center justify-end">
                      <StarIcon className="text-yellow-400 text-sm mr-1" sx={{ fontSize: "16px" }} />
                      <p className="font-bold text-gray-900">{member.avgRating}</p>
                    </div>
                    <p className="text-xs text-gray-600">{member.totalSurveys} surveys</p>
                    <p className="text-xs text-red-600">{member.negativePercentage}% negative</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No team member data available</p>
          )}
        </div>

      </div>
    </div>
  );
}
